/**
 * One prompt: fill in the blanks, copy the result.
 *
 * The finished prompt sits in a box that updates while you type, rather than
 * behind a "Generate" button. Someone who can see their own words landing in
 * the prompt understands what the form is for without being told, and there
 * is no step where they fill everything in and then wonder why nothing
 * happened.
 *
 * Answers are kept in this browser per prompt, so coming back to copy it again
 * — into a second AI, or next week — does not mean typing the business
 * description out a third time. That is a convenience only: storage can be
 * refused (private windows), so every read and write tolerates failing.
 */

import { useEffect, useMemo, useState } from "react";
import { useParams } from "wouter";
import { Check, Copy, Loader2 } from "lucide-react";
import { activeFields, chosenOption, fillPrompt, type PromptChoice } from "@shared/prompts";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import PromptsUpgradeNotice from "@/components/PromptsUpgradeNotice";
import { BUTTON } from "@/lib/buttonStyles";
import { trpc } from "@/lib/trpc";
import NotFound from "@/pages/NotFound";

const storageKey = (slug: string) => `yss.prompt.${slug}`;

function readSaved(slug: string): Record<string, string> {
  try {
    const raw = window.localStorage.getItem(storageKey(slug));
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeSaved(slug: string, values: Record<string, string>) {
  try {
    window.localStorage.setItem(storageKey(slug), JSON.stringify(values));
  } catch {
    /* Storage refused. The form still works; it just will not remember. */
  }
}

/** Choices in declared order, bunched by group for <optgroup>. */
function groupChoices(choices: PromptChoice[]): [string | undefined, PromptChoice[]][] {
  const groups: [string | undefined, PromptChoice[]][] = [];
  for (const c of choices) {
    const last = groups[groups.length - 1];
    if (last && last[0] === c.group) last[1].push(c);
    else groups.push([c.group, [c]]);
  }
  return groups;
}

const INPUT =
  "w-full rounded-md border border-border bg-card px-3 py-3 text-base text-foreground " +
  "placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-primary/40";

export default function PromptPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const query = trpc.prompts.get.useQuery({ slug }, { retry: false });

  const [values, setValues] = useState<Record<string, string>>(() => readSaved(slug));
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setValues(readSaved(slug));
  }, [slug]);

  const data = query.data;
  const prompt = data?.prompt ?? null;

  const filled = useMemo(
    () => (prompt ? fillPrompt(prompt.template, prompt.fields, values) : ""),
    [prompt, values]
  );

  /* Recomputed on every answer, so changing the dropdown swaps in the
     questions the new option needs. Answers to hidden fields are kept, not
     cleared: switching back should not mean typing them again. */
  const shownFields = useMemo(
    () => (prompt ? activeFields(prompt.template, prompt.fields, values) : []),
    [prompt, values]
  );

  const setField = (key: string, value: string) => {
    setValues(prev => {
      const next = { ...prev, [key]: value };
      writeSaved(slug, next);
      return next;
    });
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(filled);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      /* Clipboard refused. The box below is selectable, and the hint beside
         the button tells them how to copy it by hand. */
    }
  };

  if (query.error?.data?.code === "NOT_FOUND") return <NotFound />;

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader context="Prompt Library" backHref="/prompts" backLabel="All prompts" />

      <main className="container py-8 md:py-12 max-w-5xl">
        {query.isLoading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="animate-spin w-8 h-8 text-muted-foreground" aria-label="Loading" />
          </div>
        ) : query.isError || !data ? (
          <p className="text-base text-foreground/75 py-10">
            We could not load this prompt just now. Please refresh the page to try again.
          </p>
        ) : (
          <>
            <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2">
              {data.card.category}
            </p>
            <h1 className="font-['Fraunces'] text-3xl md:text-4xl font-bold text-foreground leading-tight mb-3">
              {data.card.title}
            </h1>
            <p className="text-base md:text-lg text-foreground/75 leading-relaxed max-w-3xl">
              {data.card.summary}
            </p>

            {data.access !== "member" || !prompt ? (
              <div className="mt-8 max-w-3xl">
                <PromptsUpgradeNotice
                  access={data.access === "member" ? "upgrade" : data.access}
                  promptTitle={data.card.title}
                />
              </div>
            ) : (
              <div className="mt-8 grid gap-8 lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] lg:items-start">
                {/* Step 1 */}
                <section aria-labelledby="step-1">
                  <h2 id="step-1" className="font-['Fraunces'] text-2xl font-semibold text-foreground mb-1">
                    1. Fill in the blanks
                  </h2>
                  <p className="text-base text-muted-foreground mb-5">
                    Leave any blank you are not sure about. The prompt still works.
                  </p>

                  <div className="space-y-5">
                    {shownFields.map(f => {
                      const id = `field-${f.key}`;
                      return (
                        <div key={f.key}>
                          <label htmlFor={id} className="block text-base font-semibold text-foreground">
                            {f.label}
                          </label>
                          {f.help ? (
                            <p id={`${id}-help`} className="text-sm text-muted-foreground mt-0.5">
                              {f.help}
                            </p>
                          ) : null}
                          {f.choices ? (
                            /* A native select rather than a custom listbox: it
                               is the control people already know how to use,
                               and on a phone it opens the system picker with
                               large, readable rows. */
                            <select
                              id={id}
                              value={chosenOption(f, values)?.label ?? ""}
                              aria-describedby={f.help ? `${id}-help` : undefined}
                              onChange={e => setField(f.key, e.target.value)}
                              className={`${INPUT} mt-2 min-h-12 cursor-pointer`}
                            >
                              {groupChoices(f.choices).map(([group, choices]) =>
                                group ? (
                                  <optgroup key={group} label={group}>
                                    {choices.map(c => (
                                      <option key={c.label} value={c.label}>
                                        {c.label}
                                      </option>
                                    ))}
                                  </optgroup>
                                ) : (
                                  choices.map(c => (
                                    <option key={c.label} value={c.label}>
                                      {c.label}
                                    </option>
                                  ))
                                )
                              )}
                            </select>
                          ) : f.multiline ? (
                            <textarea
                              id={id}
                              rows={3}
                              value={values[f.key] ?? ""}
                              placeholder={f.placeholder ? `For example: ${f.placeholder}` : undefined}
                              aria-describedby={f.help ? `${id}-help` : undefined}
                              onChange={e => setField(f.key, e.target.value)}
                              className={`${INPUT} mt-2 resize-y`}
                            />
                          ) : (
                            <input
                              id={id}
                              type="text"
                              value={values[f.key] ?? ""}
                              placeholder={f.placeholder ? `For example: ${f.placeholder}` : undefined}
                              aria-describedby={f.help ? `${id}-help` : undefined}
                              onChange={e => setField(f.key, e.target.value)}
                              className={`${INPUT} mt-2 min-h-12`}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </section>

                {/* Step 2. Sticky on a wide screen so the result stays in view
                    while the form scrolls; on a phone it simply follows the
                    form, which is the order the steps happen in anyway. */}
                <section aria-labelledby="step-2" className="lg:sticky lg:top-32">
                  <h2 id="step-2" className="font-['Fraunces'] text-2xl font-semibold text-foreground mb-1">
                    2. Copy your prompt
                  </h2>
                  <p className="text-base text-muted-foreground mb-4">
                    Then paste it into a new chat in ChatGPT, Claude, Gemini or
                    whichever AI you use.
                  </p>

                  <button
                    type="button"
                    onClick={copy}
                    className={`${BUTTON.primary} w-full sm:w-auto`}
                    aria-live="polite"
                  >
                    {copied ? (
                      <>
                        <Check className="w-5 h-5" aria-hidden="true" /> Copied. Now paste it into your AI.
                      </>
                    ) : (
                      <>
                        <Copy className="w-5 h-5" aria-hidden="true" /> Copy prompt
                      </>
                    )}
                  </button>

                  {/* readOnly, not disabled, so it can still be focused and
                      selected by hand if the clipboard is refused. */}
                  <textarea
                    readOnly
                    value={filled}
                    aria-label="Your finished prompt"
                    onFocus={e => e.currentTarget.select()}
                    rows={16}
                    className="mt-4 w-full rounded-md border border-border bg-muted px-3 py-3 font-mono text-sm leading-relaxed text-foreground"
                  />

                  {prompt.howToUse ? (
                    <div className="mt-4 rounded-lg border border-border bg-secondary/40 p-4">
                      <p className="text-base font-semibold text-foreground mb-1">What happens next</p>
                      <p className="text-base text-foreground/75 leading-relaxed">{prompt.howToUse}</p>
                    </div>
                  ) : null}
                </section>
              </div>
            )}
          </>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}
